<template lang="pug">
  div
    payout-top(:company="company" :searchText.sync="searchText" :isFetching="isFetching" :filters="filters" :companyTiers="companyTiers" @updateFilter="updateFilter")
    v-divider.basic300
    payout-list-filters-chip(v-if="filterChanged" :company="company" :filters="filters" :companyTiers="companyTiers" @updateFilter="updateFilter")
    payout-bulk-actions(:selectedItems="selectedItems" :allItems="payouts" :isFetching="isFetching" :isLoading="isLoading" @updateAll="updateAllTask" @export="exportCSV" @markAsPaid="markAsPaid")
    v-divider.basic300.mb-3
    v-container.pa-0(style="max-width: 600px")
      v-layout.my-6.px-2.pt-2(v-if="isFetching")
        v-row.fill-height.ma-0(align='center' justify='center')
          v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
      .content-layout(v-else)
        v-layout.my-6.px-2.pt-2(v-if="payouts.length == 0")
          v-row.fill-height.ma-0(align='center' justify='center')
            div.mx-auto
              v-img.mx-auto(:src="require('@/assets/images/icon_empty.png')" style="width:80px; height:80px" contain)
              div.p2.basic600--text.text-center No payout found.
        div(v-else)
          payout-item(v-for="(payout, index) in payouts" ref="payoutItem" :key="payout.id" :item="payout" :companyTiers="companyTiers" :selected="selectedItems.some(selected => selected.id === payout.id)" @updateData="updateTask" @removeData="removeTask")
        div.py-3.px-4
          y-pagination-custom(v-if="payouts.length > 0" v-model="page" :parentPageSize.sync="pageSize" :count="count" :text="'Show payouts'")
</template>

<script>
import { mapState, mapActions } from 'vuex'
import PayoutTop from '@/components/resellers/payouts/PayoutTop.vue'
import PayoutListFiltersChip from '@/components/resellers/payouts/PayoutListFiltersChip.vue'
import PayoutBulkActions from '@/components/resellers/payouts/PayoutBulkActions.vue'
import PayoutItem from '@/components/resellers/payouts/PayoutItem.vue'

export default {
  name: 'PayoutProcessing',
  components: { PayoutTop, PayoutListFiltersChip, PayoutItem, PayoutBulkActions },
  props: {
    company: {
      type: Object,
      default: () => {
        return {}
      }
    },
    companyTiers: {
      type: Array,
      required: true
    },
    processingCount: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    isFetching: false,
    isLoading: false,
    fetchUrl: '/members/payouts/',
    searchCountDown: null,
    page: 1,
    pageSize: 20,
    count: 0,
    searchText: '',
    payouts: [],
    selectedItems: [],
    filters: {
      sort: 'newest',
      range: {
        name: 'All',
        value: null,
        dates: null
      },
      tier: null
    },
    filterChanged: false
  }),
  computed: {
    ...mapState('user', ['user', 'member']),
    filterParams() {
      return {
        company: this.company.id,
        status: 'processed',
        ordering: this.filters.sort === 'newest' ? '-created' : 'created',
        page: this.page,
        page_size: this.pageSize,
        search: this.searchText,
        tier: this.filters.tier ? this.filters.tier : null,
        range: this.filters.range.dates ? null : this.filters.range.value,
        created_before: this.filters.range.dates ? this.filters.range.dates[1] : null,
        created_after: this.filters.range.dates ? this.filters.range.dates[0] : null
      }
    }
  },
  async mounted() {
    this.page = this.$route.query.page ? parseInt(this.$route.query.page) : 1
    await this.fetchPayouts()
    this.$emit('update:processingCount', this.count)
  },
  watch: {
    page(val) {
      history.pushState({}, null, location.pathname + this.$route.hash + '?page=' + val)
      if (!this.isFetching) {
        this.fetchPayouts()
      }
    },
    pageSize() {
      this.page = 1
      this.fetchPayouts()
    },
    searchText: {
      handler() {
        if (this.searchCountDown) {
          clearTimeout(this.searchCountDown)
        }
        this.searchCountDown = setTimeout(() => {
          this.page = 1
          this.fetchPayouts()
        }, 200)
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPayouts() {
      this.isFetching = true
      return this.axios
        .get(this.fetchUrl, { params: this.filterParams })
        .then(resp => {
          this.payouts = resp.data.results
          this.count = resp.data.count
          this.isFetching = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    removeTask(item) {
      const index = this.selectedItems.findIndex(selected => selected.id === item.id)
      if (index !== -1) {
        this.selectedItems.splice(index, 1)
      }
    },
    updateTask(item) {
      const index = this.selectedItems.findIndex(selected => selected.id === item.id)
      if (index === -1) {
        this.selectedItems.push(item)
      } else {
        this.selectedItems[index] = item
      }
    },
    updateAllTask(val) {
      this.payouts.forEach(payout => {
        val ? this.updateTask(payout) : this.removeTask(payout)
      })
    },
    updateFilter(newVal) {
      this.filters = newVal
      this.filterChanged = true
      this.fetchPayouts()
    },
    exportCSV() {
      this.isLoading = true
      let exportDomain = 'https://export-test.yezza.io'
      if (process.env.VUE_APP_ENV === 'prod') {
        exportDomain = 'https://export.yezza.io'
      }
      const ids = this.selectedItems.map(item => item.id).join(',')
      const url = `${exportDomain}/members/payouts/export_csv/?company=${this.company.id}&ids=${ids}`
      this.$axios.post(`/users/${this.user.id}/request_otk/`)
        .then(response => {
          const exportUrl = url + `&key=${response.data.key}`
          if (this.$isMobile) {
            window.open(exportUrl)
          } else {
            location.href = exportUrl
          }
        })
        .catch(() => {})
        .finally(() => this.isLoading = false)
    },
    async markAsPaid() {
      this.isLoading = true
      const actions = this.selectedItems.map(item => {
        return this.axios.patch(`members/payouts/${item.id}?company=${this.company.id}`, { status: 'completed' })
      })
      try {
        await Promise.all([...actions])
        await this.fetchPayouts()
        this.selectedItems = []
        this.isLoading = false
        this.showMessage(['success', this.$t('success_message.payout_updated')])
        this.$emit('update:processingCount', this.payouts.length)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    }
  }
}
</script>